import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import CollapsibleTable from '../../components/table';
import axios from 'axios';
import config from '../../config/config';
import { useSession } from '../../components/hooks/useSession';
import SimpleSuccessSpinner from '../../components/LoadingSpinner';
import handleOpenSweetAlert from '../../components/handleOpenSweetAlert';
import FilterComponent from '../../components/FilterComponent';
import ModalSolicitudes from '../../components/ModalSolicitudes/ModalSolicitudes';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import EditNoteIcon from '@mui/icons-material/EditNote';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { Box, Typography, Stack, ToggleButtonGroup, ToggleButton, Badge } from '@mui/material';
import ConfirmacionPrecios from '../ConfirmacionPrecios/ConfirmacionPrecios';

export default function SolicitudesCorregir() {
  const { loggedInUsername, nombreEmpresa, idEmpresa, idPerfil } = useSession();
  const apiUrl = config.apiBaseUrl;

  const usuarioId = useMemo(() => Number(sessionStorage.getItem('UsuarioId') || 0), []);

  const [data, setData] = useState({ contenido: [] });
  const [loadingData, setLoadingData] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [tipoCorreccion, setTipoCorreccion] = useState('PRECIOS');

  // 🔢 CONTEOS PARA LOS BADGES
  const [conteos, setConteos] = useState({ rechazadas: 0, sinClasificar: 0, precios: 0 });

  const fetchConteos = useCallback(async () => {
    if (!apiUrl || !loggedInUsername) return;
    try {
      const resp = await axios.post(`${apiUrl}/api/solicitudesCorregirConteos`, {
        p_UsuarioActual: loggedInUsername,
        p_UsuarioId: usuarioId,
        p_Empresa: nombreEmpresa,
        p_IdEmpresa: idEmpresa,
        p_IdPerfil: idPerfil
      });
      setConteos(resp.data);
    } catch (e) {
      console.error("Error al cargar conteos:", e);
    }
  }, [apiUrl, loggedInUsername, usuarioId, nombreEmpresa, idEmpresa, idPerfil]);

  const updateConteo = useCallback((tipo, total) => {
    setConteos(prev => {
      const key = tipo === 'RECHAZADAS' ? 'rechazadas' : 
                  tipo === 'SIN_CLASIFICAR' ? 'sinClasificar' : 
                  tipo === 'PRECIOS' ? 'precios' : null;
      if (!key || prev[key] === total) return prev;
      return { ...prev, [key]: total };
    });
  }, []);

  useEffect(() => {
    fetchConteos();
  }, [fetchConteos]); // Solo al montar

  /* MODAL CORREGIR EXCLUSIVO */
  const [modalSolicitudesOpen, setModalSolicitudesOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [initialFiles, setInitialFiles] = useState([]);

  const baseFilters = useMemo(() => ({
    FECHA_INICIO: '',
    FECHA_FIN: '',
    FOLIO: '',
    EMPRESA: '',
    PROVEEDOR: '',
  }), []);

  const [tempFilterData, setTempFilterData] = useState(baseFilters);
  const [filterData, setFilterData] = useState(baseFilters);

  const [tableParams, setTableParams] = useState({
    page: 1,
    pageSize: 25,
    globalQuery: '',
    sortField: 'ID',
    sortDir: 'DESC',
    hiddenCols: ['TOTAL_REGISTROS', 'AUTSOLPAGOID', 'CONCEPTOPAGO', 'OBSERVACIONES', 'RFC', 'IDDATOSERP', 'VALOR_CONCEPTO', 'VALOR_OBSERVACIONES', 'DOCS', 'ID', 'EMPRESAID']
  });

  const ejecutarBusquedaManual = useCallback((filtrosPasados = null) => {
    setFilterData(filtrosPasados || tempFilterData);
    setTableParams(prev => ({ ...prev, page: 1 }));
  }, [tempFilterData]);

  const abortRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!apiUrl || !loggedInUsername) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoadingData(true);
    try {
      const { page, pageSize, globalQuery, sortField, sortDir } = tableParams;

      const payload = {
        p_Busqueda: filterData.FOLIO || globalQuery || filterData.PROVEEDOR || '',
        p_FechaDesde: filterData.FECHA_INICIO || null,
        p_FechaHasta: filterData.FECHA_FIN || null,
        p_Start: (page - 1) * pageSize,
        p_Length: pageSize,
        p_SortColumn: sortField || 'ID',
        p_SortDir: sortDir || 'DESC',
        p_Empresa: nombreEmpresa,
        p_UsuarioActual: loggedInUsername,
        p_TIPO_CORRECCION: tipoCorreccion
      };

      const response = await axios.post(`${apiUrl}/solicitudesCorregirTabla`, payload, { signal: controller.signal });
      const decodedData = response.data?.contenido || [];
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const normalizedData = decodedData.map(row => {
        let diasVenc = row.DIAS_VENCIMIENTO;
        let vencActivo = row.VENCIMIENTO_ACTIVO;
        
        // Recalcular días si tenemos fecha de vencimiento (corrige desfases del backend)
        if (row.FECHA_VENCIMIENTO) {
          const parts = row.FECHA_VENCIMIENTO.split('T')[0].split('-');
          if (parts.length === 3) {
            const vDate = new Date(parts[0], parts[1] - 1, parts[2]);
            const diff = Math.ceil((vDate - today) / (1000 * 60 * 60 * 24));
            diasVenc = diff;
            vencActivo = diff >= 0 ? 1 : 0;
          }
        }
        
        return { 
          ...row, 
          DOCS: 1,
          DIAS_VENCIMIENTO: diasVenc,
          DIASRESTANTES: diasVenc,
          VENCIMIENTO_ACTIVO: vencActivo
        };
      });

      setData({
        ...response.data,
        contenido: normalizedData
      });

      // Sincronizar el badge con el total de la tabla
      updateConteo(tipoCorreccion, response.data.total || normalizedData.length);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Error al cargar correcciones:', err);
      handleOpenSweetAlert('No se pudieron cargar los datos.', 'error');
    } finally {
      if (abortRef.current === controller) {
        setLoadingData(false);
        abortRef.current = null;
      }
    }
  }, [apiUrl, filterData, nombreEmpresa, loggedInUsername, tipoCorreccion, tableParams, updateConteo]);

  const handleTableStateChange = useCallback((newState) => {
    const newGQ = newState.globalQuery || '';
    const newSF = newState.sortConfig?.field || null;
    const newSD = newState.sortConfig?.direction || null;
    const newPage = (newState.page ?? 0) + 1;
    const newPageSize = newState.rowsPerPage || 25;

    setTableParams(prev => {
      const resetPage = prev.globalQuery !== newGQ ||
        prev.sortField !== newSF ||
        prev.sortDir !== newSD ||
        prev.pageSize !== newPageSize;

      const nextRow = {
        page: resetPage ? 1 : newPage,
        pageSize: newPageSize,
        globalQuery: newGQ,
        sortField: newSF,
        sortDir: newSD,
        hiddenCols: newState.hiddenCols || []
      };

      if (JSON.stringify(prev) === JSON.stringify(nextRow)) return prev;
      return nextRow;
    });
  }, []);

  useEffect(() => {
    setTableParams(prev => ({ ...prev, page: 1 }));
  }, [tipoCorreccion]);

  useEffect(() => {
    fetchData();
  }, [fetchData, filterData, tableParams, tipoCorreccion]);

  const handleCorregir = useCallback(async (row) => {
    setLoadingAction(true);
    try {
      const resp = await axios.post(`${apiUrl}/pagosAutDescargar`, { id: row.ID });
      const { adjuntos = [] } = resp.data || {};

      const filesMapped = adjuntos.map(a => {
        let cat = (a.category || '').toUpperCase().trim();
        if (cat === 'DATOS BANCARIOS' || cat === 'CARATULAS') cat = 'CARATULA';

        return {
          id: a.archivo_id,
          name: a.filename,
          category: cat,
          filepath: a.filepath || '', // 📁 Guardamos la ruta para filtrar
          isExisting: true,
          serverSide: true
        };
      }).filter(f => {
        const name = (f.name || '').toUpperCase();
        const cat = (f.category || '').toUpperCase();
        const path = (f.filepath || '').toUpperCase();

        // 🛡️ Filtro definitivo: 
        // 1. Por categoría ("FACTURA")
        // 2. Por nombre (si contiene "CFDI" o "FACTURA" sin ser Orden de Compra)
        // 3. Por CARPETA (si la ruta contiene "\FACTURA\" o "/FACTURA/")
        const isInvoice =
          cat.includes('FACTURA') ||
          name.includes('CFDI') ||
          (name.includes('FACTURA') && !name.includes('ORDEN')) ||
          path.includes('\\FACTURA\\') ||
          path.includes('/FACTURA/');

        return !isInvoice;
      });

      setSelectedRow(row);
      setInitialFiles(filesMapped);
      setModalSolicitudesOpen(true);
    } catch (e) {
      console.error(e);
      handleOpenSweetAlert('Error al cargar datos de la solicitud', 'error');
    } finally {
      setLoadingAction(false);
    }
  }, [apiUrl]);

  const buttons = useMemo(() => {
    if (tipoCorreccion === 'SIN_CLASIFICAR') {
      return [
        {
          id: 'gestionar',
          label: 'Gestionar',
          onClick: handleCorregir,
          icon: <FactCheckIcon />,
          buttonColor: '#0655A5',
          buttonFontColor: '#fff',
          isVisible: true,
          title: 'Gestionar clasificación y archivos'
        }
      ];
    }

    return [
      {
        id: 1,
        label: 'Corregir',
        onClick: handleCorregir,
        icon: <EditNoteIcon />,
        buttonColor: '#0655A5',
        buttonFontColor: '#fff',
        isVisible: true,
      }
    ];
  }, [handleCorregir, tipoCorreccion]);

  const empresaIdPersist = useMemo(() => {
    if (Array.isArray(idEmpresa)) return idEmpresa[0] || 0;
    const n = Number(idEmpresa);
    return Number.isFinite(n) ? n : 0;
  }, [idEmpresa]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2, gap: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ borderBottom: '1px solid #e0e0e0', pb: 1 }}>
        <ToggleButtonGroup
          value={tipoCorreccion}
          exclusive
          onChange={(e, val) => val && setTipoCorreccion(val)}
          size="small"
          sx={{
            bgcolor: '#f5f7fa',
            p: '4px',
            borderRadius: '10px',
            '& .MuiToggleButton-root': {
              px: 3,
              py: 0.8,
              border: 'none',
              borderRadius: '8px !important',
              fontWeight: 700,
              fontSize: '0.9rem',
              textTransform: 'none',
              gap: 1.2,
              color: '#555',
              transition: 'all 0.2s ease',
              '&.Mui-selected': {
                bgcolor: '#0655A5',
                color: '#fff',
                boxShadow: '0 2px 6px rgba(6, 85, 165, 0.3)',
                '&:hover': { bgcolor: '#033C75' }
              }
            }
          }}
        >
          <ToggleButton value="PRECIOS">
            <Badge badgeContent={conteos.precios} color="info" sx={{ '& .MuiBadge-badge': { right: -3, top: 2 } }}>
              <TaskAltIcon fontSize="small" />
            </Badge>
            Confirmar Precios
          </ToggleButton>
47587
        </ToggleButtonGroup>
      </Stack>

      {tipoCorreccion === 'PRECIOS' ? (
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <ConfirmacionPrecios idPerfil={idPerfil} onTotalChange={(total) => updateConteo('PRECIOS', total)} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden', gap: 2 }}>
          <FilterComponent
            filterData={tempFilterData}
            setFilterData={setTempFilterData}
            handleClick={ejecutarBusquedaManual}
            initialFilters={['FECHA_INICIO', 'FECHA_FIN', 'FOLIO', 'PROVEEDOR']}
            showExportButton={false}
          />

          <CollapsibleTable
            loading={loadingData}
            dataTable={data}
            buttons={buttons}
            columnLabels={{
              ID: 'ID',
              FOLIO: 'Orden',
              EMPRESA: 'Empresa',
              FECHASOLICITUD: 'Fecha OC',
              PROVEEDOR: 'Proveedor',
              MONTOSOLICITADO: 'Monto',
              DIVISA: 'Divisa',
              DEPARTAMENTO: 'Departamento',
              CENTROCOSTO: 'Centro Costo',
              NUMEROCC: 'N° CC',
              SOLICITANTE: 'Solicitante',
              ESTADO_CORRECCION: 'Estado',
              MOTIVO: 'Motivo Rechazo',
            }}
            hiddenFields={['DETALLE', 'REQUIERE_RECLASIFICACION', 'AUTSOLPAGOID', 'RFC', 'IDDATOSERP', 'conceptoPago', 'observaciones', 'DOCS', 'NUMERO_ECONOMICO']}
            persistEnabled={true}
            persistKey="SolicitudesCorregir"
            persistVista="layout"
            defaultSortField="ID"
            defaultSortDir="DESC"
            usuarioId={usuarioId}
            empresaId={empresaIdPersist}
            serverPagination={true}
            onStateChange={handleTableStateChange}
            totalServerRows={data?.total || 0}
          />

          <ModalSolicitudes
            open={modalSolicitudesOpen}
            onClose={() => setModalSolicitudesOpen(false)}
            rowData={selectedRow}
            initialFiles={initialFiles}
            onSaved={() => fetchData(false)}
            isResend={selectedRow?.ESTATUS === 'Cancelada'}
            guidedMode={true} // Pasamos flag para el nuevo modo
            reclassificationOnly={tipoCorreccion === 'SIN_CLASIFICAR'}
            correctionMetadata={{
              motivo: selectedRow?.MOTIVO,
              detalle: selectedRow?.DETALLE,
              secciones: selectedRow?.SECCIONES,
              reclasificar: selectedRow?.REQUIERE_RECLASIFICACION
            }}
          />

          <SimpleSuccessSpinner open={loadingAction} message="Cargando..." />
        </Box>
      )}
    </Box>
  );
}
